type RGBAFloat = { r: number; g: number; b: number; a: number };

type GradientStop = { color: RGBAFloat; position: number };

type SolidBackground = { type: 'SOLID'; value: RGBAFloat };

type GradientBackground = {
  type:
    | 'GRADIENT_LINEAR'
    | 'GRADIENT_RADIAL'
    | 'GRADIENT_ANGULAR'
    | 'GRADIENT_DIAMOND';
  value: { gradientStops: GradientStop[] };
};

type MediaBackground = {
  type: 'IMAGE' | 'VIDEO';
  value: {
    imageUrl: string;
    scaleMode: string;
    scalingFactor: number;
  };
};

export type BackgroundSpec =
  | SolidBackground
  | GradientBackground
  | MediaBackground;

const _convertRGBAtoHEX = (channels: number[]) => {
  const hexChannels = channels.map((entry: any) =>
    `0${entry.toString(16)}`.slice(-2),
  );

  const hex = `#${hexChannels.join('')}`;

  if (hex.length > 7) {
    return hex.slice(0, 7);
  }

  return hex;
};

const _parseRGBA = (raw: string): number[] => {
  const channels = raw
    .replace(/rgba|rgb|\(|\)/g, '')
    .split(/,\s*/g)
    .map((entry: any, index: any) => {
      const number = parseFloat(entry);

      return index === 3 ? Math.floor(number * 255) : number;
    });

  return channels;
};

const _getRGBAColor = (color: RGBAFloat) => {
  const { r, g, b, a } = color;

  return `rgba(${parseInt(String(r * 255), 10)}, ${parseInt(
    String(g * 255),
    10,
  )}, ${parseInt(String(b * 255), 10)}, ${a})`;
};

const _getHexColor = (data: RGBAFloat) =>
  _convertRGBAtoHEX(_parseRGBA(_getRGBAColor(data)));

const _gradientPositionToPercent = (position: number) =>
  `${(position * 100).toFixed(0)}%`;

const _diamondGradientPositionToPercent = (position: number) =>
  `${((position * 100) / 2).toFixed(0)}%`;

const _gradientPositionToDeg = (position: number) =>
  `${(position * 360).toFixed(0)}deg`;

const _getImageScaleMode = ({
  scaleMode,
  scalingFactor,
}: {
  scaleMode: string;
  scalingFactor: number;
}) => {
  switch (scaleMode) {
    case 'FILL': {
      return '50% / cover';
    }
    case 'FIT': {
      return '50% / contain';
    }
    case 'CROP': {
      return '';
    }
    case 'TILE': {
      return `0% 0% / ${(scalingFactor * 100).toFixed(2)}px ${(
        scalingFactor * 100
      ).toFixed(2)}px`;
    }
  }
};

export const getBackground = ({
  type,
  value,
}: {
  type: BackgroundSpec['type'];
  value:
    | SolidBackground['value']
    | GradientBackground['value']
    | MediaBackground['value'];
}) => {
  switch (type) {
    case 'SOLID': {
      return _getHexColor(value as RGBAFloat);
    }
    case 'GRADIENT_LINEAR': {
      const colors = (value as GradientBackground['value']).gradientStops
        .map(
          (gradientStop: any) =>
            `${_getRGBAColor(gradientStop.color)} ${_gradientPositionToPercent(
              gradientStop.position,
            )}`,
        )
        .join(',');

      return `linear-gradient(180deg, ${colors})`;
    }
    case 'GRADIENT_RADIAL': {
      const colors = (value as GradientBackground['value']).gradientStops
        .map(
          (gradientStop: any) =>
            `${_getRGBAColor(gradientStop.color)} ${_gradientPositionToPercent(
              gradientStop.position,
            )}`,
        )
        .join(',');

      return `radial-gradient(50% 50% at 50% 50%, ${colors})`;
    }
    case 'GRADIENT_ANGULAR': {
      const colors = (value as GradientBackground['value']).gradientStops
        .map(
          (gradientStop: any) =>
            `${_getRGBAColor(gradientStop.color)} ${_gradientPositionToDeg(
              gradientStop.position,
            )}`,
        )
        .join(',');

      return `conic-gradient(from 180deg at 50% 50%, ${colors})`;
    }
    case 'GRADIENT_DIAMOND': {
      const colors = (value as GradientBackground['value']).gradientStops
        .map(
          (gradientStop: any) =>
            `${_getRGBAColor(
              gradientStop.color,
            )} ${_diamondGradientPositionToPercent(gradientStop.position)}`,
        )
        .join(',');

      return `linear-gradient(to bottom right, ${colors}) bottom right / 50% 50% no-repeat, linear-gradient(to bottom left, ${colors}) bottom left / 50% 50% no-repeat, linear-gradient(to top left, ${colors}) top left / 50% 50% no-repeat, linear-gradient(to top right, ${colors}) top right / 50% 50% no-repeat`;
    }
    case 'IMAGE': {
      const v = value as MediaBackground['value'];
      return `url(${v.imageUrl}) ${_getImageScaleMode({
        scaleMode: v.scaleMode,
        scalingFactor: v.scalingFactor,
      })} no-repeat`;
    }
    case 'VIDEO': {
      const v = value as MediaBackground['value'];
      return `url(${v.imageUrl}) ${_getImageScaleMode({
        scaleMode: v.scaleMode,
        scalingFactor: v.scalingFactor,
      })} no-repeat`;
    }
  }
};

export const getPropertyValue = ({
  type,
  value,
}: {
  type: BackgroundSpec['type'];
  value:
    | SolidBackground['value']
    | GradientBackground['value']
    | MediaBackground['value'];
}) => {
  switch (type) {
    case 'SOLID': {
      return _getHexColor(value as RGBAFloat)
        .replace('#', '')
        .toUpperCase();
    }
    case 'GRADIENT_LINEAR': {
      return 'Linear';
    }
    case 'GRADIENT_RADIAL': {
      return 'Radial';
    }
    case 'GRADIENT_ANGULAR': {
      return 'Angular';
    }
    case 'GRADIENT_DIAMOND': {
      return 'Diamond';
    }
    case 'IMAGE': {
      return 'Image';
    }
    case 'VIDEO': {
      return 'Video';
    }
  }
};
